import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import UiButton from "components/base/UiButton";
import {
  AllocationPreview,
  EditableLine,
  formatAmountInputFromCents,
  parseAmountInputToCents,
  SyncableJob,
} from "./quickbooksSyncUtils";

type Props = {
  addManualLine: () => void;
  allocationPreview: AllocationPreview;
  openJobPicker: () => void;
  removeLine: (lineId: string) => void;
  removeSelectedJob: (jobId: string) => void;
  seedJobId: string;
  selectedJobs: SyncableJob[];
  updateLine: (
    lineId: string,
    field: "itemName" | "itemDescription" | "amountInput",
    value: string
  ) => void;
  updateSyncAmount: (lineId: string, value: string) => void;
  visibleLines: EditableLine[];
};

export default function QuickbooksSelectedWorkTable({
  addManualLine,
  allocationPreview,
  openJobPicker,
  removeLine,
  removeSelectedJob,
  seedJobId,
  selectedJobs,
  updateLine,
  updateSyncAmount,
  visibleLines,
}: Props) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        mb={1}
      >
        <Box>
          <Typography variant="h6">Selected Work</Typography>
          <Typography variant="body2" color="text.secondary">
            Item Amount comes from the selected work. Edit Sync Amount to change what is sent.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <UiButton
            uiVariant="primary-blue"
            onClick={openJobPicker}
            startIcon={<AddIcon fontSize="small" />}
          >
            Add Job
          </UiButton>
          <UiButton
            uiVariant="primary-blue"
            onClick={addManualLine}
            startIcon={<AddIcon fontSize="small" />}
          >
            Add Line
          </UiButton>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={1.5}>
        {selectedJobs.map((selectedJob) => (
          <Chip
            key={selectedJob.id}
            size="small"
            label={`${selectedJob.externalId} - ${selectedJob.name}`}
            onDelete={
              selectedJob.id === seedJobId
                ? undefined
                : () => removeSelectedJob(selectedJob.id)
            }
            color="default"
            variant="outlined"
            sx={{
              backgroundColor: "#fff",
              borderColor: "rgba(0, 0, 0, 0.23)",
            }}
          />
        ))}
      </Stack>

      <TableContainer>
        <Table
          size="small"
          sx={{
            tableLayout: "fixed",
            "& .MuiTableCell-root": {
              px: 0.75,
              py: 0.75,
              verticalAlign: "top",
            },
          }}
        >
          <TableHead sx={{ display: "table-header-group" }}>
            <TableRow>
              <TableCell sx={{ width: "8%" }}>Job ID</TableCell>
              <TableCell sx={{ width: "17%" }}>Item</TableCell>
              <TableCell sx={{ width: "40%" }}>Item Description</TableCell>
              <TableCell sx={{ width: "14%" }}>Item Amount</TableCell>
              <TableCell sx={{ width: "16%" }}>Sync Amount</TableCell>
              <TableCell sx={{ width: "5%" }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleLines.map((line) => {
              const syncAmount = allocationPreview.byLine.get(line.id) ?? 0;
              const syncAmountValue = line.hasSyncAmountOverride
                ? line.syncAmountInput || ""
                : formatAmountInputFromCents(syncAmount);

              return (
              <TableRow key={line.id}>
                <TableCell>
                  <Typography variant="body2" sx={{ pt: 0.75 }}>
                    {line.sourceType === "manual" ? "Manual" : line.jobExternalId}
                  </Typography>
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    size="small"
                    value={line.itemName}
                    onChange={(e) => updateLine(line.id, "itemName", e.target.value)}
                  />
                  {line.groupName && (
                    <Typography variant="caption" color="text.secondary">
                      {line.groupName}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    minRows={2}
                    value={line.itemDescription}
                    onChange={(e) => updateLine(line.id, "itemDescription", e.target.value)}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    size="small"
                    inputProps={{ inputMode: "decimal" }}
                    value={line.amountInput}
                    InputProps={{ readOnly: line.sourceType !== "manual" }}
                    onChange={(e) => {
                      if (line.sourceType === "manual") {
                        updateLine(line.id, "amountInput", e.target.value);
                      }
                    }}
                    onBlur={(e) => {
                      if (line.sourceType === "manual" && e.target.value.trim()) {
                        updateLine(
                          line.id,
                          "amountInput",
                          formatAmountInputFromCents(parseAmountInputToCents(e.target.value))
                        );
                      }
                    }}
                    sx={line.sourceType !== "manual" ? { "& .MuiInputBase-root": { bgcolor: "grey.100" } } : undefined}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    size="small"
                    inputProps={{ inputMode: "decimal" }}
                    value={syncAmountValue}
                    onChange={(e) => updateSyncAmount(line.id, e.target.value)}
                    onBlur={(e) => {
                      if (e.target.value.trim()) {
                        updateSyncAmount(
                          line.id,
                          formatAmountInputFromCents(parseAmountInputToCents(e.target.value))
                        );
                      }
                    }}
                  />
                  {line.hasSyncAmountOverride && (
                    <Typography variant="caption" color="primary.main" display="block" sx={{ mt: 0.25 }}>
                      Manual override
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  <IconButton onClick={() => removeLine(line.id)}>
                    <DeleteIcon color="error" />
                  </IconButton>
                </TableCell>
              </TableRow>
              );
            })}
            {visibleLines.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography variant="body2" color="text.secondary">
                    No QuickBooks lines selected.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
